import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import PageHeader from '../components/common/PageHeader';

const FaqsPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: "FAQ's" }
  ];

  // FAQ Accordion state
  const [openIndex, setOpenIndex] = useState(0);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqGroups = [
    {
      title: "General Questions",
      faqs: [
        {
          question: "What is Blockfirst Capital?",
          answer: "Blockfirst Capital is a pioneering blockchain investment firm revolutionizing the finance industry. We provide unparalleled transparency, security, and efficiency through cutting-edge blockchain technology, offering diverse investment products and services to help clients achieve their financial goals."
        },
        {
          question: "How does Blockfirst Capital differ from traditional investment firms?",
          answer: "Unlike traditional investment firms, Blockfirst Capital leverages blockchain technology to provide enhanced transparency, security, and efficiency. Our approach combines traditional financial expertise with innovative blockchain solutions, offering clients unique investment opportunities in both traditional and digital assets."
        },
        {
          question: "What investment services does Blockfirst Capital offer?",
          answer: "We offer a comprehensive range of investment services including blockchain funds, energy investments, shares trading, agricultural funds, portfolio management, financial advising, and more. Our diverse offerings allow clients to diversify their investments across various sectors and asset classes."
        }
      ]
    },
    {
      title: "Investment Process",
      faqs: [
        {
          question: "What is the minimum investment amount?",
          answer: "The minimum investment amount varies depending on the specific fund or service. Typically, our funds have minimum investments ranging from $10,000 to $100,000. Please contact our investment advisors for specific details about the funds you're interested in."
        },
        {
          question: "How are investments managed at Blockfirst Capital?",
          answer: "Our investments are managed by a team of experienced professionals who combine traditional financial expertise with deep knowledge of blockchain technology. We employ rigorous risk management strategies, thorough due diligence processes, and continuous monitoring to ensure optimal performance of our investment portfolios."
        },
        {
          question: "What returns can I expect from my investments?",
          answer: "Investment returns vary based on the specific fund, market conditions, and risk levels. While we aim to deliver competitive returns, all investments carry risk, including the risk of loss. We provide transparent reporting on past performance, though past performance is not indicative of future results."
        }
      ]
    },
    {
      title: "Blockchain Technology",
      faqs: [
        {
          question: "How does Blockfirst Capital use blockchain technology?",
          answer: "We integrate blockchain technology across our operations to enhance transparency, security, and efficiency. This includes using blockchain for transaction verification, asset tokenization, smart contracts for automated processes, and secure record-keeping. This technology allows us to offer innovative investment products and streamlined operations."
        },
        {
          question: "Are my investments secure with blockchain technology?",
          answer: "Blockchain technology provides enhanced security through its distributed ledger system, cryptographic protocols, and immutable record-keeping. However, like all investments, there are inherent risks. We implement robust security measures and risk management strategies to protect client investments to the greatest extent possible."
        },
        {
          question: "Do I need to understand blockchain to invest with Blockfirst Capital?",
          answer: "No, you don't need to understand blockchain technology to invest with us. Our investment advisors will guide you through the process and explain everything in clear, accessible terms. We handle all the technical aspects while you focus on your investment goals."
        }
      ]
    }
  ];

  return (
    <>
      <PageHeader 
        title="Frequently Asked Questions" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-6">
              Have Questions? We've Got Answers
            </h2>
            <p className="text-lg text-gray-600">
              Find answers to the most commonly asked questions about our investment services, processes, and technology. If you can't find what you're looking for, please contact our team.
            </p>
          </motion.div>
          
          <div className="max-w-4xl mx-auto space-y-12">
            {faqGroups.map((group, groupIndex) => (
              <motion.div 
                key={groupIndex}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: groupIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-heading font-semibold text-primary mb-6 border-b border-gray-200 pb-2">
                  {group.title}
                </h3>
                <div className="space-y-4">
                  {group.faqs.map((faq, faqIndex) => {
                    const index = groupIndex * 100 + faqIndex;
                    const isOpen = openIndex === index;
                    
                    return (
                      <div key={faqIndex} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <button
                          className="w-full flex justify-between items-center p-6 text-left focus:outline-none"
                          onClick={() => toggleAccordion(index)}
                        >
                          <h4 className="text-lg font-medium text-primary">{faq.question}</h4>
                          <span className="text-secondary ml-4">
                            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                          </span>
                        </button>
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                        >
                          <div className="p-6 pt-0 text-gray-600 border-t border-gray-100">
                            {faq.answer}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-20 bg-primary text-white p-8 rounded-xl text-center max-w-3xl mx-auto"
          >
            <h3 className="text-2xl font-heading font-semibold mb-4">Still Have Questions?</h3>
            <p className="mb-6">
              If you couldn't find the answer to your question, our team is here to help. Contact us for personalized assistance.
            </p>
            <div className="flex justify-center">
              <a href="/contact-us" className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors">
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FaqsPage; 